// src/app/forgot-password/forgot-left/forgot-left.component.ts
import { Component, OnInit, OnDestroy, ViewChildren, QueryList, ElementRef } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { take } from 'rxjs/operators';
import { ForgotPasswordService } from '../../../shared/services/forgotpassword/forgotpassword';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ToastService } from '../../../shared/services/toast/toast.service';

@Component({
  selector: 'app-leftsection',
  imports: [ReactiveFormsModule, CommonModule],
  templateUrl: './leftsection.html',
  styleUrl: './leftsection.scss',
})
export class Leftsection implements OnInit, OnDestroy {
  stage = 1;
  emailForm!: FormGroup;
  otpDigits: string[] = ['', '', '', ''];
  passwordForm!: FormGroup;
  error = '';
  loading = false;

  // Timer properties
  resendTimer = 0; // in seconds
  private timerInterval: any;
  private readonly RESEND_COOLDOWN = 60; // 60 seconds

  @ViewChildren('otpInput') otpInputs!: QueryList<ElementRef<HTMLInputElement>>;
  constructor(
    private fb: FormBuilder,
    protected svc: ForgotPasswordService,
    private router: Router,
    private toastService: ToastService,
  ) {}

  ngOnInit() {
    this.emailForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
    });

    this.passwordForm = this.fb.group(
      {
        password: ['', [Validators.required, Validators.minLength(8)]],
        confirm: ['', [Validators.required]],
      },
      { validators: this.passwordsMatch },
    );

    this.svc.stage$.subscribe((s) => {
      this.stage = s.stage;
      // Start timer when entering stage 2 (code verification)
      if (s.stage === 2 && this.resendTimer === 0) {
        this.startResendTimer();
      }
      // Clear timer when leaving stage 2
      if (s.stage !== 2) {
        this.clearTimer();
      }
    });
  }

  ngOnDestroy() {
    this.clearTimer();
  }

  // Step 1: request OTP
  requestOtp() {
    if (this.emailForm.invalid) {
      this.emailForm.markAllAsTouched();
      const emailControl = this.emailForm.get('email');
      if (emailControl?.errors?.['required']) {
        this.toastService.error('Email is required.', 'Validation Error');
      } else if (emailControl?.errors?.['email']) {
        this.toastService.error('Please enter a valid email address.', 'Validation Error');
      } else {
        this.toastService.error('Please enter a valid email address.', 'Validation Error');
      }
      return;
    }
    this.loading = true;
    this.error = '';
    const email = this.emailForm.value.email;
    this.svc
      .requestOtp(email)
      .pipe(take(1))
      .subscribe({
        next: () => {
          // clear the email input after successful request
          this.loading = false;
          this.emailForm.reset();
          // Timer will start automatically when stage changes to 2
        },
        error: (err) => {
          this.loading = false;
          this.error = err?.message || 'Failed to request code';
          // Error toast is already shown by ForgotPasswordService
        },
      });
  }

  // OTP input helper
  onOtpInput(e: Event, idx: number) {
    const el = e.target as HTMLInputElement;
    const v = el.value.replace(/\D/g, '').slice(0, 1);
    this.otpDigits[idx] = v;
    el.value = v;
    if (v && idx < 3) {
      const next = this.otpInputs.toArray()[idx + 1];
      if (next) {
        next.nativeElement.focus();
      }
    }
  }
  onOtpKeydown(e: KeyboardEvent, idx: number) {
    const code = e.key;
    if (code === 'Backspace' && !this.otpDigits[idx] && idx > 0) {
      const prev = this.otpInputs.toArray()[idx - 1];
      if (prev) {
        prev.nativeElement.focus();
      }
    }
  }

  verifyOtp() {
    const otp = this.otpDigits.join('');
    if (otp.length < 4) {
      this.toastService.error('Please enter the complete 4-digit code.', 'Validation Error');
      return;
    }
    this.loading = true;
    this.error = '';
    this.svc
      .verifyOtp(otp)
      .pipe(take(1))
      .subscribe({
        next: () => {
          // clear otp inputs after successful verification
          this.loading = false;
          this.otpDigits = ['', '', '', ''];
          // clear DOM values if inputs are present
          this.otpInputs?.forEach((i) => (i.nativeElement.value = ''));
        },
        error: (err) => {
          this.loading = false;
          this.error = err?.message || 'Invalid code';
          // Error toast is already shown by ForgotPasswordService
        },
      });
  }

  submitPassword() {
    // Mark all fields as touched to trigger validation
    this.passwordForm.markAllAsTouched();

    // Update validity to ensure all errors are detected
    Object.keys(this.passwordForm.controls).forEach((key) => {
      const control = this.passwordForm.get(key);
      control?.updateValueAndValidity({ emitEvent: false });
    });
    this.passwordForm.updateValueAndValidity({ emitEvent: false });

    if (this.passwordForm.invalid) {
      const passwordControl = this.passwordForm.get('password');
      const confirmControl = this.passwordForm.get('confirm');

      // Check form-level errors first (like mismatch)
      if (this.passwordForm.errors?.['mismatch']) {
        this.toastService.error('Passwords do not match.', 'Validation Error');
        return;
      }

      // Check password field errors
      if (passwordControl?.invalid) {
        if (passwordControl.errors?.['required']) {
          this.toastService.error('Password is required.', 'Validation Error');
          return;
        } else if (passwordControl.errors?.['minlength']) {
          this.toastService.error('Password must be at least 8 characters.', 'Validation Error');
          return;
        }
      }

      // Check confirm password field errors
      if (confirmControl?.invalid) {
        if (confirmControl.errors?.['required']) {
          this.toastService.error('Please confirm your password.', 'Validation Error');
          return;
        }
      }

      // Generic error if we can't determine the specific issue
      this.toastService.error('Please fill in all required fields correctly.', 'Validation Error');
      return;
    }
    this.loading = true;
    this.svc
      .resetPassword(this.passwordForm.value.password)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.loading = false;
          // clear password inputs after success
          this.passwordForm.reset();
        },
        error: (err) => {
          this.loading = false;
          this.error = err?.message || 'Failed resetting password';
          // Error toast is already shown by ForgotPasswordService
        },
      });
  }

  passwordsMatch(group: FormGroup) {
    const p = group.get('password')?.value;
    const c = group.get('confirm')?.value;
    return p === c ? null : { mismatch: true };
  }

  continueFromSuccess() {
    // maybe redirect to login
    this.router.navigate(['/login']);
    this.svc.restart();
  }

  /**
   * Navigate back to login page
   */
  goToLogin(): void {
    this.router.navigate(['/login'], { replaceUrl: true });
  }

  resendOtp() {
    // Don't allow resend if timer is still running
    if (this.resendTimer > 0) {
      return;
    }

    const email = this.svc.current.email || this.emailForm.value.email;
    if (!email) {
      this.error = 'No email to resend to';
      return;
    }

    this.loading = true;
    this.error = '';
    this.svc
      .requestOtp(email)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.loading = false;
          this.otpDigits = ['', '', '', ''];
          this.otpInputs?.toArray()[0]?.nativeElement.focus();
          // Restart the timer after successful resend
          this.startResendTimer();
        },
        error: (err) => {
          this.loading = false;
          this.error = err?.message ?? 'Resend failed';
        },
      });
  }

  /**
   * Start the resend timer countdown
   */
  private startResendTimer() {
    this.clearTimer(); // Clear any existing timer
    this.resendTimer = this.RESEND_COOLDOWN;

    this.timerInterval = setInterval(() => {
      this.resendTimer--;
      if (this.resendTimer <= 0) {
        this.clearTimer();
      }
    }, 1000);
  }

  /**
   * Clear the resend timer
   */
  private clearTimer() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
    this.resendTimer = 0;
  }

  /**
   * Get formatted timer display (MM:SS)
   */
  get timerDisplay(): string {
    if (this.resendTimer <= 0) {
      return '';
    }
    const minutes = Math.floor(this.resendTimer / 60);
    const seconds = this.resendTimer % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  /**
   * Check if resend button should be disabled
   */
  get canResend(): boolean {
    return this.resendTimer === 0 && !this.loading;
  }
}
